import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button } from "@mui/material";
import { AttachMoney, ReceiptLong, VerifiedUser, Translate, Calculate } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PublicHeaderDark from "components/PublicHeaderDark";
import PublicFooterDark from "components/PublicFooterDark";
import RevealOnScroll from "components/RevealOnScroll";
import DarkHeroBackdrop from "components/DarkHeroBackdrop";
import { SG, BG, BODY_FONT, PANEL, TEXT, PURPLE, PURPLE_LIGHT, MAXW, btnP, btnG } from "theme/publicDarkTokens";

const AboutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [liveStats, setLiveStats] = useState(null);
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/api/public/stats")
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) setLiveStats(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDataReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  const milestones = [2019, 2020, 2021, 2022, 2023, 2024, 2025].map((year) => ({
    year: String(year),
    title: t(`about.timeline.${year}.title`),
    description: t(`about.timeline.${year}.description`),
  }));

  const values = [
    { icon: <AttachMoney sx={{ fontSize: 36, color: "white" }} />, bg: "linear-gradient(135deg, #10b981 0%, #059669 100%)", title: t("about.values.affordability.title"), description: t("about.values.affordability.description") },
    { icon: <ReceiptLong sx={{ fontSize: 36, color: "white" }} />, bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", title: t("about.values.transparency.title"), description: t("about.values.transparency.description") },
    { icon: <VerifiedUser sx={{ fontSize: 36, color: "white" }} />, bg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", title: t("about.values.freeClaim.title"), description: t("about.values.freeClaim.description") },
    { icon: <Translate sx={{ fontSize: 36, color: "white" }} />, bg: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", title: t("about.values.englishFriendly.title"), description: t("about.values.englishFriendly.description") },
  ];

  const stats = [
    { value: new Date().getFullYear() - 2019 + "+", label: t("about.stats.yearsExperience") },
    { value: liveStats?.packagesDelivered || "10,000+", label: t("about.stats.packagesDelivered") },
    { value: liveStats?.happyCustomers || "500+", label: t("about.stats.happyCustomers") },
    { value: t("about.stats.operatingDaysValue"), label: t("about.stats.operatingDays"), accent: true },
  ];

  const team = [
    { key: "ceo", avatar: "K", color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
    { key: "coo", avatar: "S", color: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
    { key: "gm", avatar: "A", color: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
    { key: "cr", avatar: "S", color: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" },
  ];

  const renderStatValue = (value, accent) => {
    if (accent) {
      return (
        <Box sx={{ fontFamily: SG, fontWeight: 700, fontSize: 36, color: PURPLE_LIGHT, letterSpacing: "-.04em", lineHeight: 1, mb: 1.25 }}>{value}</Box>
      );
    }
    const str = String(value);
    const hasPlus = str.endsWith("+");
    return (
      <Box sx={{ fontFamily: SG, fontWeight: 700, fontSize: 46, color: TEXT, letterSpacing: "-.04em", lineHeight: 1, mb: 1.25 }}>
        {hasPlus ? str.slice(0, -1) : str}
        {hasPlus && <Box component="span" sx={{ color: PURPLE, fontSize: 32 }}>+</Box>}
      </Box>
    );
  };

  return (
    <Box sx={{ background: BG, fontFamily: BODY_FONT, overflowX: "hidden" }}>
      <PublicHeaderDark />

      {/* ══════════ HERO ══════════ */}
      <Box component="section" sx={{ position: "relative", overflow: "hidden", background: BG, textAlign: "center" }}>
        <DarkHeroBackdrop />
        <Box sx={{ position: "relative", maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 }, py: { xs: 8, md: 11 } }}>
          <RevealOnScroll y={28} duration={700} enabled={dataReady}>
            <Typography
              component="h1"
              sx={{
                fontFamily: SG,
                fontSize: "clamp(32px, 4vw, 52px)",
                fontWeight: 700,
                letterSpacing: "-.03em",
                mb: 2.25,
                background: "linear-gradient(130deg, #C084FC 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("about.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(248,245,255,.56)", maxWidth: 720, mx: "auto", lineHeight: 1.75 }}>
              {t("about.subtitle")}
            </Typography>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ MISSION & VISION ══════════ */}
      <Box component="section" sx={{ background: BG, py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 }, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 4 }}>
          {[
            { titleKey: "about.mission.title", descKey: "about.mission.description" },
            { titleKey: "about.vision.title", descKey: "about.vision.description" },
          ].map((block, i) => (
            <RevealOnScroll key={i} y={24} delay={i * 100}>
              <Box sx={{ p: 4, height: "100%", borderRadius: "20px", background: PANEL, border: "1.5px solid rgba(124,58,237,.2)" }}>
                <Typography sx={{ fontFamily: SG, fontSize: 24, fontWeight: 700, color: PURPLE_LIGHT, mb: 1.5 }}>
                  {t(block.titleKey)}
                </Typography>
                <Typography sx={{ fontSize: 15, color: "rgba(248,245,255,.56)", lineHeight: 1.85 }}>
                  {t(block.descKey)}
                </Typography>
              </Box>
            </RevealOnScroll>
          ))}
        </Box>
      </Box>

      {/* ══════════ STATS ══════════ */}
      <Box component="section" sx={{ background: PANEL, borderTop: "1px solid rgba(248,245,255,.05)", borderBottom: "1px solid rgba(248,245,255,.05)" }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 }, py: 7 }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 5 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>{t("about.stats.title")}</Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.45)" }}>{t("about.stats.subtitle")}</Typography>
          </RevealOnScroll>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: { xs: 4, md: 0 } }}>
            {stats.map((stat, i) => (
              <RevealOnScroll key={i} y={20} delay={i * 100} enabled={dataReady} sx={{ textAlign: "center", px: { md: 3 }, borderRight: { md: i < 3 ? "1px solid rgba(248,245,255,.06)" : "none" } }}>
                {renderStatValue(stat.value, stat.accent)}
                <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.4)" }}>{stat.label}</Typography>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ TIMELINE ══════════ */}
      <Box component="section" sx={{ background: BG, py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>{t("about.timeline.title")}</Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.45)" }}>{t("about.timeline.subtitle")}</Typography>
          </RevealOnScroll>
          <Box>
            {milestones.map((milestone, index) => (
              <RevealOnScroll key={index} y={20} delay={Math.min(index * 60, 300)}>
                <Box sx={{ display: "flex", mb: 3, alignItems: "center", flexDirection: { xs: "column", md: "row" } }}>
                  <Box sx={{ minWidth: 110, textAlign: { xs: "center", md: "right" }, pr: { md: 3.5 }, mb: { xs: 1.5, md: 0 } }}>
                    <Typography sx={{ fontFamily: SG, fontSize: 24, fontWeight: 700, color: PURPLE_LIGHT }}>{milestone.year}</Typography>
                  </Box>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: PURPLE, flexShrink: 0, mx: { xs: "auto", md: 0 }, mb: { xs: 1.5, md: 0 } }} />
                  <Box
                    sx={{
                      ml: { xs: 0, md: 4 },
                      width: { xs: "100%", md: "auto" },
                      flexGrow: 1,
                      p: 2.5,
                      borderRadius: "14px",
                      background: PANEL,
                      border: "1.5px solid rgba(248,245,255,.08)",
                      transition: "all .3s ease",
                      "&:hover": { transform: "translateX(8px)", borderColor: "rgba(124,58,237,.4)" },
                    }}
                  >
                    <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, mb: 0.5 }}>{milestone.title}</Typography>
                    <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.5)" }}>{milestone.description}</Typography>
                  </Box>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ VALUES ══════════ */}
      <Box component="section" sx={{ background: PANEL, py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>{t("about.valuesSection.title")}</Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.45)" }}>{t("about.valuesSection.subtitle")}</Typography>
          </RevealOnScroll>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 2.5 }}>
            {values.map((value, index) => (
              <RevealOnScroll key={index} y={24} delay={index * 80}>
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    height: "100%",
                    borderRadius: "16px",
                    background: BG,
                    border: "1.5px solid rgba(248,245,255,.08)",
                    transition: "all .3s ease",
                    "&:hover": { transform: "translateY(-6px)", borderColor: "rgba(124,58,237,.4)" },
                  }}
                >
                  <Box sx={{ width: 64, height: 64, borderRadius: "16px", mx: "auto", mb: 2, background: value.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {value.icon}
                  </Box>
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, mb: 1 }}>{value.title}</Typography>
                  <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.5)", lineHeight: 1.7 }}>{value.description}</Typography>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ TEAM ══════════ */}
      <Box component="section" sx={{ background: BG, py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>{t("about.teamTitle")}</Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.45)" }}>{t("about.teamSubtitle")}</Typography>
          </RevealOnScroll>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 2.5 }}>
            {team.map((member, index) => (
              <RevealOnScroll key={index} y={24} delay={index * 80}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    height: "100%",
                    borderRadius: "16px",
                    background: PANEL,
                    border: "1.5px solid rgba(248,245,255,.08)",
                    transition: "all .3s ease",
                    "&:hover": { transform: "translateY(-8px)", borderColor: "rgba(124,58,237,.4)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      fontSize: "1.8rem",
                      fontWeight: 700,
                      color: "#fff",
                      background: member.color,
                      mx: "auto",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {member.avatar}
                  </Box>
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, mb: 0.25 }}>{t(`about.team.${member.key}.name`)}</Typography>
                  <Typography sx={{ fontSize: 13, color: PURPLE_LIGHT, fontWeight: 600, mb: 1 }}>{t(`about.team.${member.key}.position`)}</Typography>
                  <Typography sx={{ fontSize: 13, color: "rgba(248,245,255,.5)", lineHeight: 1.7 }}>{t(`about.team.${member.key}.description`)}</Typography>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ CTA ══════════ */}
      <Box
        component="section"
        sx={{ position: "relative", overflow: "hidden", py: { xs: 8, md: 11 }, textAlign: "center", background: "linear-gradient(140deg, #1A0845 0%, #3B1181 50%, #5B21B6 100%)" }}
      >
        <Box sx={{ position: "absolute", width: 700, height: 700, borderRadius: "50%", border: "1px solid rgba(255,255,255,.04)", top: -350, right: -240, pointerEvents: "none" }} />
        <Box sx={{ position: "relative", maxWidth: 700, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={24}>
            <Typography component="h2" sx={{ fontFamily: SG, fontSize: "clamp(26px, 3vw, 38px)", fontWeight: 700, color: "#fff", letterSpacing: "-.03em", mb: 2 }}>
              {t("about.cta.title")}
            </Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(255,255,255,.6)", mb: 4.5 }}>{t("about.cta.subtitle")}</Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              <Button sx={{ ...btnP, background: "#fff", color: "#3B1181", "&:hover": { background: "#F0EAFF" } }} onClick={() => navigate("/calculator")}>
                <Calculate sx={{ fontSize: 17 }} />
                {t("about.cta.calculateRate")}
              </Button>
              <Button sx={{ ...btnG, borderColor: "rgba(255,255,255,.3)" }} onClick={() => navigate("/services")}>
                {t("about.cta.viewServices")}
              </Button>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      <PublicFooterDark />
    </Box>
  );
};

export default AboutPage;
